import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import {
  analyticsControllerGetOrdersDrilldownCustomers,
  analyticsControllerGetOrdersDrilldownOrders,
} from '@/generated/korza-api-client';
import type {
  OrdersDrilldownCustomersResponseDto,
  OrdersDrilldownOrdersResponseDto,
} from '@/generated/korza-api-client/types.gen';
import {
  DRILLDOWN_MOBILE_PAGE_SIZE,
  type DrilldownQuery,
} from '@/lib/orders-drilldown';

/** A page of the drill-down list, tagged with the view it was fetched for. */
export type OrdersDrilldownInfinitePage =
  | (OrdersDrilldownOrdersResponseDto & { view: 'orders' })
  | (OrdersDrilldownCustomersResponseDto & { view: 'customers' });

interface UseOrdersDrilldownInfiniteInput {
  tenantSlug: string;
  /** Null means closed: nothing is requested. */
  query: DrilldownQuery | null;
}

/**
 * "Carregar mais" list of the mobile drill-down sheet. The page lives outside
 * the key, so a new search, segment or view is a new list from page 1.
 */
export function useOrdersDrilldownInfinite({
  tenantSlug,
  query,
}: UseOrdersDrilldownInfiniteInput): UseInfiniteQueryResult<
  InfiniteData<OrdersDrilldownInfinitePage>
> {
  return useInfiniteQuery<
    OrdersDrilldownInfinitePage,
    Error,
    InfiniteData<OrdersDrilldownInfinitePage>,
    readonly unknown[],
    number
  >({
    queryKey: [
      'ordersDrilldownInfinite',
      tenantSlug,
      query?.view,
      { ...query, page: undefined },
    ],
    queryFn: async ({ pageParam }) => {
      if (query === null) throw new Error('Drill-down is closed');
      const page = { page: pageParam, limit: DRILLDOWN_MOBILE_PAGE_SIZE };
      if (query.view === 'customers') {
        const res = await analyticsControllerGetOrdersDrilldownCustomers({
          path: { tenantSlug },
          query: { ...query, ...page },
          throwOnError: true,
        });
        return { ...res.data, view: 'customers' };
      }
      const res = await analyticsControllerGetOrdersDrilldownOrders({
        path: { tenantSlug },
        query: { ...query, ...page },
        throwOnError: true,
      });
      return { ...res.data, view: 'orders' };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.limit < lastPage.total
        ? lastPage.page + 1
        : undefined,
    enabled: query !== null,
  });
}
